import { useLayoutEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import type { CrmPage } from './crmBridge'
import { RailGlyph } from './CrmPageRail'
import { railEntryOn, railHref, railKey, railPages, type CrmPermissions, type MenuStyle } from './crmRail'
import { RAIL_ICONS } from './crmRailIcons'
import { CrmAccountTab } from './CrmAccountSheet'
import { MenuTab } from '../components/MenuTab'
import { useMobileMenu } from '../context/SidebarContext'
import './CrmPageRail.css'

interface Props {
  accountId: string
  page: CrmPage
  perms: CrmPermissions | null
  unread?: number
  /** Fansly accounts can show Fansly's own menu (Settings → Styling). */
  menu?: MenuStyle
}

/** How many tab bars are mounted (a page swap can briefly hold two). */
let mounted = 0

/** The tabs the phone keeps at hand (ADR 0035), by railKey; the rest, in the menu's order, sit under More. */
const PRIMARY: Record<MenuStyle, ReadonlySet<string>> = {
  onlyfans: new Set(['chats', 'collections', 'vault']),
  // Fansly style: fansly.com's phone bar keeps Home, Messages and Notifications.
  fansly: new Set(['home', 'chats', 'notifications']),
}

/**
 * The CRM page rail on the phone: a bottom tab bar under the console's React
 * pages, since the rail itself is hidden below 900px. Menu (the shell's
 * drawer) · Chats · Collections · Vault · More (Fansly style: Home · Messages ·
 * Notifications · More, the account menu's pages under More), then the accounts corner (ADR 0035, handoff-19): the open
 * Account, opening a sheet with Workspace, every Account and the full menu.
 * The account CRM page hides the shell's own tab bar (ClaimBottomBar). Like
 * the shell's bar it is pinned to the screen's bottom edge, and while it is up
 * it lends its height to --shell-tabbar-h (html.has-crm-tabbar), so pages stop
 * above it.
 */
export function CrmMobileTabs({ accountId, page: shown, perms, unread = 0, menu = 'onlyfans' }: Props) {
  const { t } = useTranslation('library')
  const { t: tc } = useTranslation('common')
  const { isMobileMenuOpen, setMobileMenuOpen } = useMobileMenu()
  const [moreOpen, setMoreOpen] = useState(false)
  const { search } = useLocation()
  const base = `/account/${encodeURIComponent(accountId)}/crm`
  const pages = railPages(perms, menu)
  const lit = railEntryOn(pages, shown, search, menu)
  const primary = pages.filter((p) => PRIMARY[menu].has(railKey(p)))
  const rest = pages.filter((p) => !PRIMARY[menu].has(railKey(p)))
  const restOn = lit !== null && rest.includes(lit)

  useLayoutEffect(() => {
    const root = document.documentElement
    mounted += 1
    root.classList.add('has-crm-tabbar')
    return () => {
      mounted -= 1
      if (mounted === 0) root.classList.remove('has-crm-tabbar')
    }
  }, [])

  return (
    <>
      <nav className="crm-mtabs" aria-label={t('crmRail.label')}>
        <MenuTab
          className="crm-mtab"
          pillClassName="crm-mtab-pill"
          labelClassName="crm-mtab-label"
          label={tc('layout.menu')}
          open={isMobileMenuOpen}
          current={isMobileMenuOpen}
          onToggle={() => setMobileMenuOpen((open) => !open)}
        />
        {primary.map((p) => {
          const on = p === lit
          const badge = p.page === 'chats' && unread > 0 ? (unread > 99 ? '99+' : String(unread)) : null
          return (
            <Link
              key={railKey(p)}
              to={railHref(base, p)}
              className={`crm-mtab ${on ? 'is-on' : ''}`}
              aria-current={on ? 'page' : undefined}
            >
              <span className="crm-mtab-pill">
                <RailGlyph id={p.icon} />
                {badge && <span className="crm-rail-badge crm-mtab-badge">{badge}</span>}
              </span>
              <span className="crm-mtab-label">{t(p.labelKey)}</span>
            </Link>
          )
        })}
        {rest.length > 0 && (
          <button type="button" className={`crm-mtab ${restOn ? 'is-on' : ''}`} aria-expanded={moreOpen} onClick={() => setMoreOpen(true)}>
            <span className="crm-mtab-pill">
              <RailGlyph id="icon-more" />
            </span>
            <span className="crm-mtab-label">{t('crmRail.more')}</span>
          </button>
        )}
        <CrmAccountTab accountId={accountId} page={shown} />
      </nav>
      {moreOpen && (
        <>
          <div className="crm-msheet-backdrop" onClick={() => setMoreOpen(false)} />
          <div className="crm-msheet" role="menu" aria-label={t('crmRail.more')}>
            <span className="crm-msheet-grip" aria-hidden="true" />
            {rest.map((p) => (
              <Link
                key={railKey(p)}
                role="menuitem"
                to={railHref(base, p)}
                className={`crm-msheet-item ${p === lit ? 'is-on' : ''}`}
                onClick={() => setMoreOpen(false)}
              >
                {RAIL_ICONS[p.icon] && <RailGlyph id={p.icon} />}
                {t(p.labelKey)}
              </Link>
            ))}
          </div>
        </>
      )}
    </>
  )
}
